import { useEffect, useState } from "react"
import ActionButton from "Components/atoms/buttons/action-button"
import TextInput from "Components/molecule/inputs/text-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import { ExpenseBudget, ExpenseBudgetCurrency, ExpenseBudgetSearchFields, employeeName } from "models/expense-budget"
import { saveExpenseBudgetExchangeRates, saveExpenseBudgetRecipients } from "slices/finance/expense-budget/thunk"

interface SettingsTabProps {
    budget: ExpenseBudget
    searchFields: ExpenseBudgetSearchFields | null
    onBudgetChanged: () => Promise<void>
    onLinesChanged: () => Promise<void>
}

const FOREIGN_CURRENCIES = [ExpenseBudgetCurrency.USD, ExpenseBudgetCurrency.EUR]

const toRateValues = (budget: ExpenseBudget): Record<string, string> =>
    Object.fromEntries(FOREIGN_CURRENCIES.map(currency =>
        [currency, budget.exchangeRates.find(r => r.currency === currency)?.rateToCad ?? ""]))

// Exchange rates (re-price foreign-currency lines) and the extra alert email recipients.
// Owner, approved amount, tax, inflation and thresholds are edited with the header's Edit button.
const SettingsTab = ({ budget, searchFields, onBudgetChanged, onLinesChanged }: SettingsTabProps) => {
    const [rates, setRates] = useState<Record<string, string>>(toRateValues(budget))
    const [recipientIds, setRecipientIds] = useState<string[]>(budget.recipients.map(r => r.employee.id))

    useEffect(() => {
        setRates(toRateValues(budget))
        setRecipientIds(budget.recipients.map(r => r.employee.id))
    }, [budget])

    const handleSaveRates = async () => {
        const payload = FOREIGN_CURRENCIES
            .filter(currency => rates[currency] !== "")
            .map(currency => ({ currency, rateToCad: Number(rates[currency]) }))
        if (!(await saveExpenseBudgetExchangeRates(budget.id, payload))) return
        await onBudgetChanged()
        await onLinesChanged()
    }

    const handleSaveRecipients = async () => {
        if (await saveExpenseBudgetRecipients(budget.id, recipientIds)) await onBudgetChanged()
    }

    const employeeOptions = (searchFields?.employees ?? []).map(e => ({ value: e.id, label: employeeName(e) }))

    return (
        <div className="d-flex flex-column gap-4">
            <div className="d-flex flex-column gap-2">
                <h5>Exchange rates to CAD</h5>
                <div className="d-flex gap-2">
                    {FOREIGN_CURRENCIES.map(currency => (
                        <TextInput key={currency} label={`1 ${currency} =  CAD`} type="number" min={0} step="0.0001"
                            value={rates[currency]} onChange={e => setRates(prev => ({ ...prev, [currency]: e.target.value }))} />
                    ))}
                </div>
                <div><ActionButton onClick={handleSaveRates}>Save rates and re-price lines</ActionButton></div>
            </div>
            <div className="d-flex flex-column gap-2">
                <h5>Alert recipients</h5>
                <span className="text-muted">The owner ({employeeName(budget.owner)}) always receives alerts. Add more people here.</span>
                <MultiSelect label="Recipients" options={employeeOptions} menuPortal
                    value={employeeOptions.filter(o => recipientIds.includes(o.value))}
                    onChange={selected => setRecipientIds(selected.map(o => o.value))} />
                <div><ActionButton onClick={handleSaveRecipients}>Save recipients</ActionButton></div>
            </div>
        </div>
    )
}

export default SettingsTab
